// victoria-client/src/views/items/detail/stock-chart/datasets.ts
import type { ChartData } from "chart.js/auto";

import type {
  RunProjectionPlan,
  ShortageStretch,
  StockHistoryPoint,
  StockProjectionPoint,
} from "@/api/stockChart";
import type { Point } from "@/components/timeseries-chart/types";

// Palette mirrors the demand chart: the brand coral (--primary) is the projection
// you trust — Without plan on item detail, Full plan in a run — with a coral band.
// Slate carries the on-hand history and the subdued Without plan layer in a run.
export const HISTORY_COLOR = "#64748b"; // slate-500
const HISTORY_FILL = "rgba(100, 116, 139, 0.10)";
const SINCE_PLAN_COLOR = "#cbd5e1"; // slate-300
export const PROJECTION_COLOR = "#f27a61"; // brand
const WITHOUT_PLAN_COLOR = "#94a3b8"; // slate-400
export const FIRMED_COLOR = "#4f46e5"; // indigo-600
const SAFETY_STOCK_COLOR = "#dc2626"; // red-600
const BAND_FILL = "rgba(242, 122, 97, 0.12)";
// One shortage colour language for the chart and the shortage list: light =
// shortage, dark = stockout. Swatches are the fills at full strength.
const SHORTAGE_FILL = "rgba(251, 184, 166, 0.7)";
const STOCKOUT_FILL = "rgba(194, 65, 12, 0.6)";
export const SEVERITY_SWATCH = {
  shortage: "bg-[#fbb8a6]",
  stockout: "bg-[#c2410c]",
} as const;

type Datasets = ChartData<"line", Point[]>["datasets"];

export type OptionalLayer = "withoutPlan" | "firmed";

const xy = <T extends { date: string }>(
  pts: T[],
  y: (p: T) => number,
): Point[] => pts.map((p) => ({ x: p.date, y: y(p) }));

const line = (label: string, data: Point[], color: string, width = 2) => ({
  label,
  data,
  borderColor: color,
  borderWidth: width,
  pointRadius: 0,
  tension: 0.3,
});

/**
 * Item detail (no `plan`): history + Without plan with its band.
 * In a run: the optional Without plan / Firmed layers, Full plan with the band,
 * the safety-stock line and the shortage fills below it.
 * `fill` targets are absolute dataset indices, taken from the built array.
 */
export function buildStockDatasets(args: {
  history: StockHistoryPoint[];
  /** The projection's first day; history from it on is what happened since. */
  startDate?: string;
  withoutPlan: StockProjectionPoint[];
  plan?: RunProjectionPlan | null;
  shown: Set<OptionalLayer>;
  labels: {
    history: string;
    since: string;
    withoutPlan: string;
    firmed: string;
    fullPlan: string;
    safetyStock: string;
    shortage: string;
    stockout: string;
  };
}): Datasets {
  const { history, startDate, withoutPlan, plan, shown, labels } = args;
  const datasets: Datasets = [];
  const push = (d: Datasets[number]) => datasets.push(d) - 1;

  // An older run's plan date lies inside the history.
  const cut = startDate ? history.findIndex((p) => p.date >= startDate) : -1;
  const before = cut < 0 ? history : history.slice(0, cut);
  push({
    ...line(
      labels.history,
      xy(before, (p) => p.quantity),
      HISTORY_COLOR,
    ),
    backgroundColor: HISTORY_FILL,
    fill: true,
  });
  if (cut >= 0)
    push(
      line(
        labels.since,
        xy(history.slice(Math.max(cut - 1, 0)), (p) => p.quantity),
        SINCE_PLAN_COLOR,
      ),
    );

  const band = (pts: StockProjectionPoint[], meanIdx: number) => {
    const label = datasets[meanIdx].label;
    for (const edge of ["upper", "lower"] as const)
      push({
        ...line(
          `${label} ${edge}`,
          xy(pts, (p) => p[edge]),
          "transparent",
          0,
        ),
        fill: meanIdx,
        backgroundColor: BAND_FILL,
      });
  };

  // History up to the plan date ends at the day before's closing: step from it
  // to the projection so an overdue receipt counted on the plan date reads as a jump.
  const join = (projection: StockProjectionPoint[]) => {
    const last = before.at(-1);
    const first = projection[0];
    if (!last || !first) return;
    push({
      ...line(
        "",
        [
          { x: last.date, y: last.quantity },
          { x: first.date, y: last.quantity },
          { x: first.date, y: first.mean },
        ],
        HISTORY_COLOR,
      ),
      tension: 0,
    });
  };

  if (!plan) {
    join(withoutPlan);
    const i = push(
      line(
        labels.withoutPlan,
        xy(withoutPlan, (p) => p.mean),
        PROJECTION_COLOR,
      ),
    );
    band(withoutPlan, i);
    return datasets;
  }

  const { firmed, fullPlan, safetyStock: ss, shortages } = plan;
  join(fullPlan);
  if (shown.has("withoutPlan"))
    push({
      ...line(
        labels.withoutPlan,
        xy(withoutPlan, (p) => p.mean),
        WITHOUT_PLAN_COLOR,
        1.5,
      ),
      borderDash: [5, 3],
    });
  if (shown.has("firmed"))
    push(
      line(
        labels.firmed,
        xy(firmed, (p) => p.mean),
        FIRMED_COLOR,
        1.5,
      ),
    );
  const fullIdx = push(
    line(
      labels.fullPlan,
      xy(fullPlan, (p) => p.mean),
      PROJECTION_COLOR,
      2.5,
    ),
  );
  band(fullPlan, fullIdx);
  const ssIdx = push({
    ...line(
      labels.safetyStock,
      xy(fullPlan, () => ss),
      SAFETY_STOCK_COLOR,
      1,
    ),
    borderDash: [2, 3],
    tension: 0,
  });
  // The server's shortage stretches pick each day's shade; the fill runs from
  // safety stock down to the service-level line (band's lower edge).
  const severity = (date: string) =>
    shortages.find((s) => s.start <= date && date < s.end)?.severity;
  const shade = (kind: ShortageStretch["severity"]) =>
    xy(fullPlan, (p) =>
      severity(p.date) === kind ? Math.min(p.lower, ss) : ss,
    );
  push({
    ...line(labels.shortage, shade("shortage"), "transparent", 0),
    tension: 0,
    fill: ssIdx,
    backgroundColor: SHORTAGE_FILL,
  });
  push({
    ...line(labels.stockout, shade("stockout"), "transparent", 0),
    tension: 0,
    fill: ssIdx,
    backgroundColor: STOCKOUT_FILL,
  });
  return datasets;
}
